import {
  captureErrorMessage,
  captureException,
} from '../../../utilities/analytics/error-reporting';
import { useMemo } from 'react';
import { RecaptchaError } from '../../../utilities/RecaptchaError';

const RECAPTCHA_SCRIPT_DOMAIN = 'www.recaptcha.net';
const FAIL_LOAD_CAPTCHA_ERROR_MESSAGE = 'Failed to load grecaptcha enterprise.';

// Returns the reCAPTCHA-script provided methods available from the window.
export const getRecaptchaMethods = () => window.grecaptcha?.enterprise;

/**
 * Loads the reCAPTCHA script if it hasn't been loaded already.
 *
 * Loading states:
 *  0. Script <src> element does not exist on the DOM.
 *  1. Script is asynchronously downloading and the reCAPTCHA methods are not available yet.
 *  2. Script has been downloaded and a 'load' event is fired for the DOM element and the reCAPTCHA methods are now available.
 *  3. Google fires a 'ready' event when the reCAPTCHA methods are available.
 *      Recaptcha can be considered to be fully ready to use at this point.
 *  4. Extra dependencies and scripts are downloaded asynchronously.
 *      This extra step should not be relevant to checkbox or score, but may be relevant for invisible.
 */

// Keep a track of all loaded scripts so the promise can be re-used by different React components/hooks:
const scriptCache = new Map<string, Promise<void>>();

export const removeInjectedRecaptchaScriptCache = (scriptId: string) =>
  scriptCache.delete(scriptId);

export const injectRecaptchaScript = async (scriptId: string, scriptSrc: string) => {
  let scriptPromise = scriptCache.get(scriptId);

  if (!scriptPromise) {
    scriptPromise = new Promise<void>((resolve, reject) => {
      const scriptElement = document.createElement('script');
      scriptElement.id = scriptId;
      scriptElement.src = `https://${RECAPTCHA_SCRIPT_DOMAIN}${scriptSrc}`;
      scriptElement.async = true;
      scriptElement.defer = true;

      scriptElement.addEventListener('load', () => {
        if (window && window.grecaptcha && window.grecaptcha.enterprise) {
          // reCAPTCHA script was injected and library 'finished' loading once ready() completes
          getRecaptchaMethods().ready(() => resolve());
        } else {
          captureErrorMessage(FAIL_LOAD_CAPTCHA_ERROR_MESSAGE);
          reject(RecaptchaError.fromMessage(FAIL_LOAD_CAPTCHA_ERROR_MESSAGE));
        }
      });

      // investigate - error undefined
      scriptElement.addEventListener('error', (errorEvent: ErrorEvent) => {
        captureException(errorEvent.error);
        reject(
          RecaptchaError.fromError(errorEvent.error || new Error(FAIL_LOAD_CAPTCHA_ERROR_MESSAGE))
        );
      });

      document.head.appendChild(scriptElement);
    });

    scriptCache.set(scriptId, scriptPromise);
  }

  return scriptPromise;
};

export const recaptchaScriptExists = (scriptId: string): boolean => {
  const script = document.getElementById(scriptId);
  return script !== null;
};

export const hideRecaptchaBadge = (scriptId: string): void => {
  const id = `${scriptId}-styles`;
  if (document.getElementById(id)) {
    return;
  }

  const styleElement = document.createElement('style');
  styleElement.id = id;
  styleElement.innerText = '.grecaptcha-badge { visibility: hidden; }';
  document.head.appendChild(styleElement);
};

export const removeRecaptchaScriptElement = (scriptId: string) => {
  const scriptElement = document.getElementById(scriptId);
  if (scriptElement) scriptElement.remove();
};

export const removeRecaptchaBadgeStyleElement = (scriptId: string) => {
  const styleElement = document.getElementById(`${scriptId}-styles`);
  if (styleElement) styleElement.remove();
};

export const removeRecaptchaBadge = () => {
  document.querySelectorAll('.grecaptcha-badge').forEach(element => element.remove());
};

type UseScoreRecaptchaProps = {
  sitekey: string;
};

export const useScoreRecaptchaScripts = ({ sitekey }: UseScoreRecaptchaProps) => {
  const scriptSrc = `/recaptcha/enterprise.js?render=${sitekey}`;

  return useMemo(
    () => ({
      injectRecaptchaScript: () => injectRecaptchaScript(sitekey, scriptSrc),
      getRecaptchaMethods: () => getRecaptchaMethods(),
      hideRecaptchaBadge: () => hideRecaptchaBadge(sitekey),
      unloadRecaptcha() {
        removeRecaptchaScriptElement(sitekey);
        removeRecaptchaBadgeStyleElement(sitekey);
        removeRecaptchaBadge();
        removeInjectedRecaptchaScriptCache(sitekey);
      },
    }),
    [sitekey, scriptSrc]
  );
};

export const useCheckboxRecaptchaScripts = () => {
  const scriptId = 'checkbox-recaptcha';
  const scriptSrc = '/recaptcha/enterprise.js?render=explicit';

  return useMemo(
    () => ({
      injectRecaptchaScript: () => injectRecaptchaScript(scriptId, scriptSrc),
      getRecaptchaMethods: () => getRecaptchaMethods(),
    }),
    []
  );
};
